import { useQuery } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { GridRowParams } from "@mui/x-data-grid";
import { mdHeight, xsHeight } from "components/AppBar";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { tabsOffset } from "components/TabLinks";
import { RegistrationDetailDrawer } from "components/waste/registration/RegistrationDetailDrawer";
import { STATE_NAMES, stateNamesByAbbreviation } from "encamp-shared/src/constants/states";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { hasValue } from "encamp-shared/src/utils/hasValue";
import {
  TenantWasteStreamsQuery,
  TenantWasteStreamsQueryVariables,
  WasteClassification,
  WasteStreamRegistrationStatus,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { usePersistedFilters } from "hooks/usePersistedFilters";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useTenant } from "hooks/useTenant";
import { useWasteStateRegistrationEnabled } from "hooks/useWasteStateRegistration";
import { useCallback, useMemo, useState } from "react";
import { StateRegistrationDialog } from "routes/Customer/Waste/StateRegistrationDialog/StateRegistrationDialog";

import { profileNumberValueGetter } from "../Facilities/Facility/formatWasteStreamProfileNumber";
import { prettyPrintWasteClassification } from "../Facilities/Facility/prettyPrintWasteClassification";
import { ProvisionalWasteStreamChip } from "../Facilities/Facility/ProvisionalWasteStreamChip";
import { TenantWasteFacilityProvider } from "../Facilities/Facility/TenantWasteFacilityProvider";
import { WasteStreamDetailsModal } from "../Facilities/Facility/WasteStreamDetails/WasteStreamDetailsModal";
import { ManifestDetailDrawer } from "../Manifests/ManifestDetailDrawer";
import { buildWasteStreamCodeColumns } from "../wasteStreamCodeColumns";
import {
  DISCREPANCY_TYPE_FILTER_COLUMN,
  buildConsolidatedIssueLabels,
  filterOpenDiscrepancies,
} from "../wasteStreamDiscrepancyIndicator";
import { WasteStreamIssuesCell } from "../WasteStreamIssuesCell";
import {
  WasteStreamRegistrationCell,
  WasteStreamStateIdCell,
} from "../WasteStreamRegistrationCell";
import { GET_TENANT_STREAM_META_QUERY, TENANT_WASTE_STREAMS_QUERY } from "./api";
import {
  CreateWasteStreamRegistrationSubmissionsDialog,
  RegistrationStream,
} from "./CreateWasteStreamRegistrationSubmissionsDialog";
import { TenantWasteStreamsDateFilter } from "./TenantWasteStreamsDateFilter";

type TenantWasteStreamRow = NonNullable<
  NonNullable<TenantWasteStreamsQuery["tenant"]>["wasteStreams"]
>["items"][number];

// Give each column a fixed initial width derived from its old `flex` weight so
// the grid overflows and scrolls horizontally instead of squishing columns to
// fit. minWidth stays low (the MUI default) so columns remain resizable to well
// under the initial width.
const COLUMN_WIDTH_PER_FLEX = 280;
const MIN_RESIZE_WIDTH = 50;

function toRegistrationStream(row: TenantWasteStreamRow): RegistrationStream {
  return {
    id: row.id,
    name: row.name,
    facilityName: row.facility?.name ?? "—",
    state: row.facility?.state ?? "",
  };
}

type TenantWasteStreamsFilters = {
  shipDateStart?: string;
  shipDateEnd?: string;
};

const STORAGE_KEY_PREFIX = "tenant-waste-streams-filters";

// Default the grid to the current calendar year on first load. At tenant
// scale (Amazon ~134K streams) the WasteLineUnified join in `getWasteStreams`
// is what dominates the query cost — partitioning by shippedDate prunes
// monthly partitions outside this window, so a bounded date range is the
// difference between a few seconds and ~11s. The all-time amount columns
// remain in the grid; only the in-window aggregates depend on this filter.
const defaultShipDateStart = new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10);
const defaultShipDateEnd = new Date().toISOString().slice(0, 10);

const wasteStreamsDefaultFilters: TenantWasteStreamsFilters = {
  shipDateStart: defaultShipDateStart,
  shipDateEnd: defaultShipDateEnd,
};

export const TenantWasteStreams = () => {
  const theme = useTheme();
  const { tenantId, tenant } = useTenant();
  const showRegistrationColumns = useWasteStateRegistrationEnabled();
  // Read-only registration columns/filters render for everyone the flag is on
  // for; the create/open submission affordances are Staff-only (ENG-15415).
  const { isStaff } = useCurrentUser();
  const { featureFlags } = useFeatureFlags();
  // Field-discrepancy warnings are GA to all waste-module tenants (visible to
  // staff AND customers); other staff-only controls on this grid stay gated on
  // `isStaff`.
  const showDiscrepancies = !!featureFlags[FeatureFlagKey.WasteModule];
  const canManageRegistrations = showRegistrationColumns && isStaff;

  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [registrationStreams, setRegistrationStreams] = useState<RegistrationStream[]>([]);
  const [selectedRows, setSelectedRows] = useState<RegistrationStream[]>([]);
  const [stateRegDialogStream, setStateRegDialogStream] = useState<TenantWasteStreamRow | null>(
    null
  );

  const openRegistrationDialog = useCallback((streams: RegistrationStream[]) => {
    setRegistrationStreams(streams);
    setRegistrationDialogOpen(true);
  }, []);

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Waste Streams",
    },
  ]);

  const storageKey = `${STORAGE_KEY_PREFIX}|${tenantId ?? ""}`;
  const { filters, setFilters } = usePersistedFilters<TenantWasteStreamsFilters>(
    storageKey,
    wasteStreamsDefaultFilters
  );

  // Default only when neither bound is set (avoids the slow all-time path); once
  // either is set, respect it so single-ended ranges survive. Key off the date
  // fields, not Object.keys — the grid also writes `omnisearch` into these params.
  const effectiveFilters = useMemo<TenantWasteStreamsFilters>(() => {
    const hasAnyBound = filters.shipDateStart != null || filters.shipDateEnd != null;
    return hasAnyBound ? filters : { ...wasteStreamsDefaultFilters, ...filters };
  }, [filters]);

  const handleDateRangeChange = useCallback(
    (next: { shipDateStart?: string; shipDateEnd?: string }) => {
      setFilters((f) => {
        f.shipDateStart = next.shipDateStart;
        f.shipDateEnd = next.shipDateEnd;
      });
    },
    [setFilters]
  );

  const dataQueryVariables = useMemo(
    () => ({
      tenantId: tenantId ?? "",
      startDate: effectiveFilters.shipDateStart,
      endDate: effectiveFilters.shipDateEnd,
    }),
    [tenantId, effectiveFilters.shipDateStart, effectiveFilters.shipDateEnd]
  );

  // No prefixSegment — the tenant route is
  // `streams/:wasteStreamId?/:wasteLineUnifiedId?`, mirroring the Manifests
  // page (`manifests/:processedManifestId?/:wasteLineUnifiedId?`). Both ids
  // sit directly under `/waste/streams/`. The per-facility view uses a
  // prefix because its route disambiguates between wasteStream and
  // manifest-line-items children; the tenant route doesn't need to.
  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("wasteStreamId");
  const {
    selectedId: selectedLineItemId,
    pushIdToUrl: pushLineItemIdToUrl,
    popIdFromUrl: popLineItemIdFromUrl,
  } = useSelectedIdInUrl("wasteLineUnifiedId");
  // Sibling of the stream/line-item selection above (route: streams/registration/:registrationId?).
  const {
    selectedId: selectedRegistrationId,
    pushIdToUrl: pushRegistrationIdToUrl,
    popIdFromUrl: popRegistrationIdFromUrl,
  } = useSelectedIdInUrl("registrationId", { prefixSegment: "registration" });
  // The URL is the single source of truth for which stream is selected.
  // We fetch the stream's facilityId via a small query keyed by id. Apollo
  // normalizes WasteStream by id, so the row-click path serves from the cache
  // populated by the grid query (no network); on deep-link or refresh the
  // query fetches the one missing row.
  const { data: selectedStreamData } = useQuery(GET_TENANT_STREAM_META_QUERY, {
    variables: { id: selectedId ?? "" },
    skip: !selectedId,
  });
  const selectedStream = selectedStreamData?.wasteStream;

  const handleRowClick = (params: GridRowParams<TenantWasteStreamRow>) => {
    pushIdToUrl(params.row.id);
  };

  const columns: OmnisearchGridColDef<TenantWasteStreamRow>[] = useMemo(() => {
    const base: OmnisearchGridColDef<TenantWasteStreamRow>[] = [
      {
        field: "facility",
        headerName: "Facility",
        filterKeyType: "wasteFacility",
        flex: 0.6,
        minWidth: 120,
        sortable: true,
        valueGetter: (_, row) => row.facility?.name ?? "",
        headerClassName: "wrapHeader",
      },
      {
        field: "name",
        headerName: "Waste Stream",
        flex: 1,
        minWidth: 100,
        sortable: true,
        headerClassName: "wrapHeader",
        renderCell: (params) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              gap: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography
                variant="body2"
                title={params.value}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: 1,
                }}
              >
                {params.value}
              </Typography>
              <ProvisionalWasteStreamChip isProvisional={params.row.isProvisional} />
            </Box>
          </Box>
        ),
      },
      {
        field: "profileNumber",
        headerName: "Profile Number",
        flex: 0.5,
        sortable: true,
        valueGetter: profileNumberValueGetter,
      },
      ...buildWasteStreamCodeColumns<TenantWasteStreamRow>(showDiscrepancies, isStaff),
      {
        field: "willNotReport",
        headerName: "Not Reportable",
        filterKeyType: "boolean",
        sortable: false,
        flex: 0.4,
        headerClassName: "wrapHeader",
        renderCell: ({ value }) => (value === true ? <Check /> : null),
      },
    ];
    if (showRegistrationColumns) {
      base.splice(
        2,
        0,
        {
          // The state-assigned registration id, split out of the Registration
          // cell into its own column. Display-only (no server sort/filter
          // wired for it); the state-registered-ID omnisearch term still
          // covers search.
          field: "stateRegisteredId",
          headerName: "State ID",
          sortable: false,
          filterable: false,
          flex: 0.4,
          headerClassName: "wrapHeader",
          renderCell: ({ row }) => (
            <WasteStreamStateIdCell
              displayStatus={row.registrationDisplayStatus}
              registration={row.registration}
            />
          ),
        },
        {
          // FK-backed registration column: status chip, the "+ State
          // Registration" matching dialog, the in-progress submission link,
          // and the detail-drawer deep link. Non-staff see the status chip
          // only, since the underlying queries/mutations are staff-only.
          field: "registrationDisplayStatus",
          headerName: "Registration",
          // Not sortable — same reason as the facility grid: the status is
          // derived after ORDER BY / LIMIT have picked the page, so a sortable
          // header would silently fall back to the default name sort.
          sortable: false,
          filterable: false,
          flex: 0.8,
          headerClassName: "wrapHeader",
          renderCell: ({ row }) => (
            <WasteStreamRegistrationCell
              displayStatus={row.registrationDisplayStatus}
              registration={row.registration}
              openSubmissionId={row.openRegistrationSubmission?.id}
              onOpenRegistration={(registrationId) => pushRegistrationIdToUrl(registrationId)}
              onOpenDialog={() => setStateRegDialogStream(row)}
              onOpenSubmission={(submissionId) =>
                window.open(
                  `/o/${tenantId}/waste/submissions/${submissionId}/edit`,
                  "_blank",
                  "noopener,noreferrer"
                )
              }
            />
          ),
        }
      );
    }
    // Consolidated, staff-only "Issues" column: every open field discrepancy +
    // field issue on the row in one sortable place (server-side sort by open
    // discrepancy count, matching this filter). The per-cell indicators above
    // stay. Gated the same as them (flag) plus isStaff, since the discrepancy
    // data is staff-only. Filtered to open (PENDING/UNRESOLVED) rows —
    // RESOLVED/IGNORED are already done and shouldn't read as outstanding work.
    if (showDiscrepancies && isStaff) {
      base.push({
        field: "discrepancyCount",
        headerName: "Issues",
        flex: 0.9,
        minWidth: 160,
        sortable: true,
        filterable: false,
        headerClassName: "wrapHeader",
        valueGetter: (_, row) =>
          buildConsolidatedIssueLabels(filterOpenDiscrepancies(row.discrepancies, isStaff)).join(
            "; "
          ),
        renderCell: ({ row }) => (
          <WasteStreamIssuesCell
            discrepancies={filterOpenDiscrepancies(row.discrepancies, isStaff)}
          />
        ),
      });
    }
    return base.map(({ flex, ...rest }) => ({
      ...rest,
      width: rest.width ?? Math.round((flex ?? 1) * COLUMN_WIDTH_PER_FLEX),
      minWidth: MIN_RESIZE_WIDTH,
    }));
  }, [showRegistrationColumns, isStaff, showDiscrepancies, pushRegistrationIdToUrl, tenantId]);

  if (!tenantId) {
    return null;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: {
          xs: `calc(100vh - ${theme.spacing(xsHeight + tabsOffset)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight + tabsOffset)})`,
        },
        minHeight: 0,
      }}
    >
      <TenantWasteStreamsDateFilter
        shipDateStart={effectiveFilters.shipDateStart}
        shipDateEnd={effectiveFilters.shipDateEnd}
        onChange={handleDateRangeChange}
      />
      <Box
        sx={{
          py: 3,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <OmnisearchDataGrid<
          TenantWasteStreamsQuery,
          TenantWasteStreamsQueryVariables,
          TenantWasteStreamRow
        >
          fillParentHeight
          persistenceKey="tenant-waste-streams-table"
          isURLDriven
          searchFieldSx={{ "& .MuiOutlinedInput-root": { backgroundColor: "background.paper" } }}
          columns={columns}
          excludeFilterColumns={["name"]}
          dataQuery={TENANT_WASTE_STREAMS_QUERY}
          getItems={(data) => data.tenant?.wasteStreams?.items ?? []}
          getCount={(data) => data.tenant?.wasteStreams?.count ?? 0}
          dataQueryVariables={dataQueryVariables}
          defaultSearch=""
          noDataMessage={"No waste streams found for this organization."}
          initialSortModel={[{ field: "name", sort: "asc" }]}
          onRowClick={handleRowClick}
          disableRowSelectionOnClick={true}
          checkboxSelection={canManageRegistrations}
          disableSelectAll={false}
          isRowSelectable={(params) =>
            !!params.row.needsSubmission && !params.row.openRegistrationSubmission
          }
          // Controlled selection so clearing `selectedRows` in the dialog's
          // onSuccess handler also unchecks the boxes after a bulk create.
          selectedIds={selectedRows.map((r) => r.id)}
          onSelectedRowsChanged={(rows) => setSelectedRows(rows.map(toRegistrationStream))}
          additionalFilterColumns={[
            {
              filterKeyType: "enumAutocomplete",
              key: "classification",
              header: "Classification",
              enumValues: Object.values(WasteClassification),
              enumPresentationFunction: (value) => prettyPrintWasteClassification(value),
            },
            {
              filterKeyType: "enumAutocomplete",
              key: "facilityState",
              header: "Facility State",
              enumValues: Object.keys(STATE_NAMES),
              enumPresentationFunction: (value) => {
                const name = stateNamesByAbbreviation.get(value);
                return name ? `${name} (${value})` : value;
              },
            },
            {
              filterKeyType: "boolean",
              key: "isProvisional",
              header: "Provisional",
            },
            ...(showRegistrationColumns
              ? [
                  {
                    filterKeyType: "boolean" as const,
                    key: "needsStateRegistration",
                    header: "Needs State Registration",
                  },
                  {
                    filterKeyType: "enumAutocomplete" as const,
                    key: "stateRegistrationStatus",
                    header: "Registration Status",
                    enumValues: Object.values(WasteStreamRegistrationStatus),
                    ignoreStateAbbreviations: true,
                  },
                  // Keep the state-registered-ID search after merging the column
                  // into "Registration" (backend contains-match, ENG-15798).
                  {
                    key: "stateRegisteredId",
                    header: "State Registered ID",
                  },
                ]
              : []),
            ...(isStaff ? [DISCREPANCY_TYPE_FILTER_COLUMN] : []),
          ]}
          commandButtons={({ search }) => [
            ...(canManageRegistrations
              ? [
                  <Button
                    key="create-submissions"
                    variant="outlined"
                    size="small"
                    disabled={selectedRows.length === 0}
                    onClick={() => openRegistrationDialog(selectedRows)}
                  >
                    {`Create submissions (${selectedRows.length})`}
                  </Button>,
                ]
              : []),
            <ExportDataButton
              key="export"
              tenantId={tenantId}
              exportType="tenantWasteStreams"
              fileName={tenant?.name ? `${tenant.name}-waste-streams` : "tenant-waste-streams"}
              // Pass raw bounds, mirroring the grid: the export backend applies
              // each bound independently, so single-ended ranges export correctly.
              // effectiveFilters already supplies year defaults when neither bound
              // is set, so this never opens an all-time scan.
              filter={{
                ...(search ? { search } : {}),
                startDate: effectiveFilters.shipDateStart,
                endDate: effectiveFilters.shipDateEnd,
              }}
              iconOnly
            />,
          ]}
        />
      </Box>

      {selectedId && selectedStream?.facilityId && (
        <TenantWasteFacilityProvider
          facilityId={selectedStream.facilityId}
          startDate={effectiveFilters.shipDateStart}
          endDate={effectiveFilters.shipDateEnd}
        >
          <WasteStreamDetailsModal
            open={!!selectedId}
            onClose={popIdFromUrl}
            wasteStreamId={selectedId}
            buildLineItemHref={(lineItemId) =>
              `/o/${tenantId}/waste/streams/${selectedId}/${lineItemId}`
            }
            onLineItemClick={pushLineItemIdToUrl}
          />
        </TenantWasteFacilityProvider>
      )}
      <ManifestDetailDrawer
        lineItemId={selectedLineItemId}
        open={!!selectedLineItemId}
        onClose={popLineItemIdFromUrl}
      />
      {registrationDialogOpen && (
        <CreateWasteStreamRegistrationSubmissionsDialog
          open
          onClose={() => setRegistrationDialogOpen(false)}
          tenantId={tenantId}
          streams={registrationStreams}
          onSuccess={() => setSelectedRows([])}
        />
      )}
      {stateRegDialogStream && (
        <StateRegistrationDialog
          open
          onClose={() => setStateRegDialogStream(null)}
          facilityId={stateRegDialogStream.facilityId ?? ""}
          facilityName={stateRegDialogStream.facility?.name ?? "—"}
          state={stateRegDialogStream.facility?.state ?? ""}
          wasteStream={stateRegDialogStream}
        />
      )}
      {/* Staff-gated: the drawer's query is staff-only, so a non-staff user
          landing on a `registration/:id` URL would open a drawer that can only
          ever error. */}
      {isStaff && hasValue(selectedRegistrationId) && (
        <RegistrationDetailDrawer
          registrationId={selectedRegistrationId}
          onClose={popRegistrationIdFromUrl}
          refetchQueries={["TenantWasteStreams"]}
        />
      )}
    </Box>
  );
};
